/**
 * Телефония: исходящие номера, правила диспозиции, транки, внутренние команды.
 *
 * Правки справочников — это SQL, они живут в cc-api. Внутренний HTTP телефонии
 * принимает только то, чему нужен живой ARI-сокет: набрать, положить, слушать (§7.10).
 * Порог дозвона в правилах называется min_talk_sec, флаг — is_reached, и других
 * имён у них нет во всём проекте (§5.2).
 */
import { z } from 'zod';
import { bigIdSchema, callDispositionSchema, isoDateTimeSchema, phoneE164Schema, refIdSchema } from './common.dto.js';
// ───────────────────────── исходящие номера ─────────────────────────
export const callerIdSchema = z.object({
    id: refIdSchema,
    trunkId: refIdSchema.nullable(),
    numberE164: phoneE164Schema,
    regionCode: z.string().nullable(),
    isActive: z.boolean(),
    dailyLimit: z.number().int().min(0).max(10_000),
    lastUsedAt: isoDateTimeSchema.nullable(),
    /** Счётчики за сегодня: по ним видно выгорание номера */
    todayCalls: z.number().int().min(0),
    todayReached: z.number().int().min(0),
    todayShortCalls: z.number().int().min(0),
});
export const createCallerIdBodySchema = z.object({
    trunkId: refIdSchema,
    numberE164: phoneE164Schema,
    regionCode: z.string().max(10).nullable().default(null),
    dailyLimit: z.number().int().min(0).max(10_000).default(300),
});
export const updateCallerIdBodySchema = z.object({
    isActive: z.boolean().optional(),
    dailyLimit: z.number().int().min(0).max(10_000).optional(),
    regionCode: z.string().max(10).nullable().optional(),
});
// ───────────────────────── правила диспозиции ─────────────────────────
/** NULL в поле правила означает «любое значение» (§7.5) */
export const dispositionRuleSchema = z.object({
    id: refIdSchema,
    priority: z.number().int().min(0).max(1000),
    trunkId: refIdSchema.nullable(),
    dialStatus: z.string().nullable(),
    hangupCause: z.number().int().nullable(),
    sipCode: z.number().int().nullable(),
    minTalkSec: z.number().int().min(0),
    disposition: callDispositionSchema,
    isReached: z.boolean(),
    /** Обрыв у диспетчера попыткой не считается: чужой Wi-Fi не портит статистику источника */
    countsAsAttempt: z.boolean(),
    note: z.string().nullable(),
    updatedAt: isoDateTimeSchema.nullable(),
});
export const createDispositionRuleBodySchema = z.object({
    priority: z.number().int().min(0).max(1000),
    trunkId: refIdSchema.nullable().default(null),
    dialStatus: z.string().max(40).nullable().default(null),
    hangupCause: z.number().int().min(0).max(255).nullable().default(null),
    sipCode: z.number().int().min(100).max(699).nullable().default(null),
    minTalkSec: z.number().int().min(0).max(3600).default(0),
    disposition: callDispositionSchema,
    isReached: z.boolean(),
    countsAsAttempt: z.boolean().default(true),
    note: z.string().max(300).nullable().default(null),
});
export const updateDispositionRuleBodySchema = createDispositionRuleBodySchema.partial();
// ─────────────────────────────── транки ───────────────────────────────
/**
 * Адрес оператора: `sip.operator.ru`, `sip.operator.ru:5060` или IP с портом.
 * Схема (`sip:`) и путь не принимаются: в `pjsip.conf` их дописывает шаблон,
 * а вписанные руками они дают неработающий `server_uri` без единой ошибки.
 */
export const sipHostSchema = z
    .string()
    .regex(/^[a-zA-Z0-9]([a-zA-Z0-9.-]{0,251}[a-zA-Z0-9])?(:[1-9][0-9]{0,4})?$/, 'ожидается адрес вида sip.operator.ru или sip.operator.ru:5060, без sip: и без пути');
/** Имя секции эндпоинта в pjsip.conf: сравнивается с ресурсом ARI один в один */
export const endpointIdSchema = z.string().regex(/^[a-zA-Z0-9_-]{1,60}$/, 'допустимы латиница, цифры, дефис и подчёркивание');
export const trunkSchema = z.object({
    id: refIdSchema,
    name: z.string(),
    endpointId: z.string().nullable(),
    provider: z.string().nullable(),
    isActive: z.boolean(),
    channelLimit: z.number().int().min(1).max(500),
    /** Регистрация упала — звонить некуда, это первый алерт (§9.4) */
    registrationState: z.string().nullable(),
    registrationCheckedAt: isoDateTimeSchema.nullable(),
    /** Адрес оператора, логин и домен — не секрет: по ним админ узнаёт транк */
    host: z.string().nullable(),
    authUsername: z.string().nullable(),
    sipDomain: z.string().nullable(),
    /**
     * Пароль оператора: только маска фиксированной длины или пустая строка.
     * Хвоста здесь нет намеренно — в отличие от адреса портала Битрикса, узнавать
     * транк по паролю не требуется, а любой показанный символ секрета лишний.
     */
    authPasswordMask: z.string(),
    /** Свой тариф транка, копейки (23.10.2026); null — транк считается по общему тарифу из настроек */
    priceAttemptKop: z.number().int().min(0).nullable(),
    priceMinuteKop: z.number().int().min(0).nullable(),
    /** Когда правили параметры подключения. Регистрацию это поле не отражает */
    updatedAt: isoDateTimeSchema.nullable(),
    /** Сколько исходящих номеров и правил диспозиции повиснет, если транк выключить */
    callerIdsCount: z.number().int().min(0),
    rulesCount: z.number().int().min(0),
});
export const createTrunkBodySchema = z.object({
    name: z.string().trim().min(2).max(60),
    host: sipHostSchema,
    authUsername: z.string().trim().max(120).nullable().default(null),
    /** Пустая строка = пароль не задан. Маска в этом поле отвергается доменом */
    authPassword: z.string().max(200).nullable().default(null),
    sipDomain: z.string().trim().max(253).nullable().default(null),
    /** Без него `trunk-watch` не сопоставит транк с эндпоинтом ARI и регистрация будет вечно «нет» */
    endpointId: endpointIdSchema.nullable().default(null),
    provider: z.string().trim().max(120).nullable().default(null),
    channelLimit: z.number().int().min(1).max(500).default(30),
    isActive: z.boolean().default(true),
    /** Свой тариф, копейки; null — общий из настроек. Потолок — как у общего тарифа */
    priceAttemptKop: z.number().int().min(0).max(100000).nullable().default(null),
    priceMinuteKop: z.number().int().min(0).max(100000).nullable().default(null),
});
/**
 * Правка. Пароль, которого не касались, в запрос не попадает вовсе: прислать
 * маску означало бы записать точки в базу и молча обрушить регистрацию.
 */
export const updateTrunkBodySchema = createTrunkBodySchema.partial();
// ─────────────────── внутренний HTTP телефонии (§7.10) ───────────────────
export const originateBodySchema = z.object({
    assignmentId: bigIdSchema,
    toPhone: phoneE164Schema,
    dispatcherId: bigIdSchema,
    /** Явный исходящий номер: обычно выбирается ротацией, здесь только для разбора инцидентов */
    callerId: phoneE164Schema.optional(),
});
export const originateResponseSchema = z.object({ callId: bigIdSchema });
export const hangupCallBodySchema = z.object({ reason: z.string().min(2).max(120) });
/** Прослушка: роль супервизора проверяется в самой телефонии, а не только в UI */
export const monitorCallBodySchema = z.object({
    supervisorId: bigIdSchema,
    mode: z.enum(['spy', 'whisper', 'barge']),
});
export const monitorCallResponseSchema = z.object({ monitorCallId: bigIdSchema });
/** GET /internal/healthz телефонии — его же читает алерт по крону */
export const telephonyHealthResponseSchema = z.object({
    ari: z.enum(['up', 'down']),
    trunks: z.array(z.object({ id: refIdSchema, registered: z.boolean() })),
    activeCalls: z.number().int().min(0),
    /** Отставание обработки событий станции: растёт — телефония не успевает */
    lagMs: z.number().int().min(0),
});
/**
 * GET /api/telephony/active-calls — кто говорит прямо сейчас.
 *
 * Длительности приходят числами с сервера, а не считаются в браузере:
 * у долго открытой вкладки часы уходят, и «разговор 4 часа» на экране почти
 * всегда означает разъехавшееся время, а не зависший звонок.
 */
export const activeCallSchema = z.object({
    callId: bigIdSchema,
    dispatcherId: bigIdSchema.nullable(),
    dispatcherName: z.string().nullable(),
    phone: z.string(),
    contactName: z.string().nullable(),
    state: z.string(),
    ageSec: z.number().int().min(0),
    /** null — абонент ещё не ответил */
    talkSec: z.number().int().min(0).nullable(),
    isManual: z.boolean(),
    /** false — слушать нечего: звук появится после ответа */
    canListen: z.boolean(),
});
export const activeCallsResponseSchema = z.object({ rows: z.array(activeCallSchema) });
//# sourceMappingURL=telephony.dto.js.map